import { useRef, useEffect, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import '../../../styles/aida-base.css';
import '../../../styles/aida-responsive.css';

/**
 * TrustedPartnersReel Component
 * "TRUSTED BY TOP PARTNERS" title with purple nebula gradient bar
 * and infinitely scrolling partner logo train
 * Desktop: Absolute positioned layout within 1024px scaler
 * Mobile: Stubbed for future implementation
 */

const PARTNER_LOGOS = [
  'https://i.imgur.com/hriWZuN.png',
  'https://i.imgur.com/SRG5ahW.png',
  'https://i.imgur.com/r5cy7YY.png',
  'https://i.imgur.com/KMYvQWD.png'
];

const CYCLE_COUNT = 10;

const PURPLE_NEBULA_GRADIENT = [
  'linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 10%, rgba(0,0,0,0) 90%, rgba(0,0,0,1) 100%)',
  'linear-gradient(90deg, rgba(80,60,255,0.55) 0%, rgba(120,70,255,0.55) 75%, rgba(180,60,255,0.55) 100%)',
  'radial-gradient(1400px at 50% 40%, rgba(255,255,255,0.03), rgba(60,0,90,0) 75%)',
  'radial-gradient(1000px at 15% 35%, rgba(100,80,255,0.40), rgba(40,0,70,0) 75%)',
  'radial-gradient(900px at 55% 60%, rgba(160,50,255,0.30), rgba(35,0,60,0) 80%)',
  'radial-gradient(1000px at 85% 70%, rgba(200,70,255,0.30), rgba(45,0,70,0) 80%)',
  'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.00) 60%)',
  'radial-gradient(circle at 50% 25%, #2a0050 0%, #1a0038 35%, #000000 75%, #000000 100%)',
].join(', ');

const PURPLE_NEBULA_BLEND =
  'normal, soft-light, overlay, screen, screen, screen, soft-light, multiply';

export const TrustedPartnersReel = () => {
  const ref = useRef(null);
  const mobileRef = useRef(null);
  const desktopCycleRef = useRef<HTMLDivElement>(null);
  const mobileCycleRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const isMobileInView = useInView(mobileRef, { once: true, amount: 0.1 });
  const [cycleWidth, setCycleWidth] = useState(0);

  useEffect(() => {
    const measureCycle = () => {
      const dw =
        desktopCycleRef.current?.getBoundingClientRect().width ?? 0;
      const mw =
        mobileCycleRef.current?.getBoundingClientRect().width ?? 0;
      const width = dw > 0 ? dw : mw;
      if (width > 0) setCycleWidth(width);
    };

    const desktopImgs = Array.from(
      desktopCycleRef.current?.querySelectorAll('img') ?? []
    );
    const mobileImgs = Array.from(
      mobileCycleRef.current?.querySelectorAll('img') ?? []
    );
    const imgs = [...desktopImgs, ...mobileImgs];
    let pending = imgs.length;

    if (pending === 0) {
      measureCycle();
      window.addEventListener('resize', measureCycle);
      return () => window.removeEventListener('resize', measureCycle);
    }

    const done = () => {
      pending--;
      if (pending <= 0) measureCycle();
    };

    imgs.forEach((img) => {
      if ((img as HTMLImageElement).complete) done();
      else {
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      }
    });

    window.addEventListener('resize', measureCycle);
    return () => window.removeEventListener('resize', measureCycle);
  }, []);

  return (
    <>
      {/* Desktop Layout */}
      <motion.section
        ref={ref}
        className="relative mx-auto hidden w-full lg:block"
        style={{
          width: '100%',
          height: '155px',
          backgroundColor: '#000000',
          overflow: 'hidden',
          position: 'relative',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          style={{ position: 'relative', width: '100%', height: '100%' }}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          {/* Title */}
          <div
            style={{
              position: 'absolute',
              left: '221px',
              top: '0px',
              width: '586px',
              zIndex: 5,
            }}
          >
            <p
              style={{
                lineHeight: '50px',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 900,
                fontSize: '40px',
                letterSpacing: '-0.5px',
                color: 'rgba(255, 255, 255, 1)',
                margin: 0,
                fontVariationSettings: "'wght' 900",
              }}
            >
              TRUSTED BY TOP PARTNERS
            </p>
          </div>

          {/* Purple Nebula Gradient Background */}
          <div
            style={{
              position: 'absolute',
              left: '-10px',
              top: '68px',
              width: '1044px',
              height: '74px',
              zIndex: 1,
              overflow: 'hidden',
              background: PURPLE_NEBULA_GRADIENT,
              backgroundBlendMode: PURPLE_NEBULA_BLEND,
            }}
          />

          {/* Logo Train — vertically centered on the gradient strip */}
          <div
            style={{
              position: 'absolute',
              left: '-9px',
              top: '68px',
              width: '1032px',
              height: '74px',
              zIndex: 5,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <div
              className="reel-track"
              style={
                {
                  display: 'flex',
                  alignItems: 'center',
                  width: 'max-content',
                  willChange: 'transform',
                  animation:
                    cycleWidth > 0
                      ? 'reelScroll 65s linear infinite'
                      : 'none',
                  '--cycle': `${cycleWidth}px`,
                } as React.CSSProperties
              }
            >
              {Array.from({ length: CYCLE_COUNT }).map((_, i) => (
                <div
                  key={i}
                  ref={i === 0 ? desktopCycleRef : undefined}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    paddingRight: '12px',
                    flexShrink: 0,
                  }}
                >
                  {PARTNER_LOGOS.map((src, j) => (
                    <img
                      key={j}
                      src={src}
                      alt=""
                      style={{
                        height: '36px',
                        width: 'auto',
                        flexShrink: 0,
                        display: 'block',
                      }}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </motion.section>

      {/* Mobile Layout — same structure, screen clips the reel */}
      <motion.section
        ref={mobileRef}
        className="relative flex w-full flex-col items-center lg:hidden"
        style={{
          width: '100%',
          backgroundColor: '#000000',
          overflow: 'hidden',
          padding: '20px 0',
        }}
        initial={{ opacity: 0 }}
        animate={isMobileInView ? { opacity: 1, y: 0 } : { opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Title */}
        <p
          style={{
            lineHeight: '50px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 900,
            fontSize: '40px',
            letterSpacing: '-0.5px',
            color: 'rgba(255, 255, 255, 1)',
            margin: 0,
            textAlign: 'center',
            fontVariationSettings: "'wght' 900",
          }}
        >
          TRUSTED BY TOP PARTNERS
        </p>

        {/* Gradient strip + Logo train */}
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '74px',
            marginTop: '18px',
            overflow: 'hidden',
          }}
        >
          {/* Purple Nebula Gradient Background */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: PURPLE_NEBULA_GRADIENT,
              backgroundBlendMode: PURPLE_NEBULA_BLEND,
            }}
          />

          {/* Logo Train */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
            }}
          >
            <div
              className="reel-track"
              style={
                {
                  display: 'flex',
                  alignItems: 'center',
                  width: 'max-content',
                  willChange: 'transform',
                  animation:
                    cycleWidth > 0
                      ? 'reelScroll 30s linear infinite'
                      : 'none',
                  '--cycle': `${cycleWidth}px`,
                } as React.CSSProperties
              }
            >
              {Array.from({ length: CYCLE_COUNT }).map((_, i) => (
                <div
                  key={i}
                  ref={i === 0 ? mobileCycleRef : undefined}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    paddingRight: '12px',
                    flexShrink: 0,
                  }}
                >
                  {PARTNER_LOGOS.map((src, j) => (
                    <img
                      key={j}
                      src={src}
                      alt=""
                      style={{
                        height: '36px',
                        width: 'auto',
                        flexShrink: 0,
                        display: 'block',
                      }}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.section>
    </>
  );
};
